import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Home, DollarSign, TrendingDown, ChevronDown, ChevronUp, Calculator, ArrowRight, Landmark, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- Amortization Engine ---
function calcMonthlyPayment(principal, annualRate, months) {
  const r = annualRate / 12;
  if (r === 0) return principal / months;
  return principal * (r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
}

function buildAmortization(principal, annualRate, totalMonths) {
  const monthly = calcMonthlyPayment(principal, annualRate, totalMonths);
  let balance = principal;
  const rows = [];
  let totalInterest = 0;
  for (let m = 1; m <= totalMonths; m++) {
    const interestPmt = balance * (annualRate / 12);
    const principalPmt = monthly - interestPmt;
    balance -= principalPmt;
    totalInterest += interestPmt;
    rows.push({
      month: m,
      payment: monthly,
      principal: principalPmt,
      interest: interestPmt,
      totalInterest,
      balance: Math.max(0, balance),
    });
  }
  return { rows, monthly, totalInterest };
}

function buildFlatFeeAmortization(principal, annualRate, flatFeePercent) {
  // First 15 years: standard amortization at full rate on 30-year schedule
  const fullMonthly = calcMonthlyPayment(principal, annualRate, 360);
  let balance = principal;
  const rows = [];
  let totalInterest = 0;

  for (let m = 1; m <= 180; m++) {
    const interestPmt = balance * (annualRate / 12);
    const principalPmt = fullMonthly - interestPmt;
    balance -= principalPmt;
    totalInterest += interestPmt;
    rows.push({
      month: m,
      payment: fullMonthly,
      principal: principalPmt,
      interest: interestPmt,
      totalInterest,
      balance: Math.max(0, balance),
      phase: 'standard',
    });
  }

  const balanceAtMidpoint = balance;
  const flatFee = principal * (flatFeePercent / 100);
  totalInterest += flatFee; // flat fee replaces second-half interest

  // Start of year 16: fee is added to the balance, then 180 equal payments at 0% interest
  balance += flatFee;
  const monthlySecondHalf = balance / 180;
  for (let m = 181; m <= 360; m++) {
    balance -= monthlySecondHalf;
    rows.push({
      month: m,
      payment: monthlySecondHalf,
      principal: monthlySecondHalf,
      interest: 0,
      totalInterest,
      balance: Math.max(0, balance),
      phase: 'flat-fee',
    });
  }

  return { rows, monthlyFirst: fullMonthly, monthlySecond: monthlySecondHalf, totalInterest, flatFee, balanceAtMidpoint };
}

const creditTiers = [
  { grade: 'A', range: '750 - 800', fee: 10, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  { grade: 'B', range: '700 - 749', fee: 15, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  { grade: 'C', range: '675 - 699', fee: 25, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
];

const fmt = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtExact = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

// --- Components ---
function StatCard({ label, value, sub, accent }) {
  return (
    <div className={`p-4 rounded-xl bg-white/5 border border-white/5 ${accent ? 'border-emerald-500/20 bg-emerald-500/5' : ''}`}>
      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-xl font-bold ${accent ? 'text-emerald-400' : 'text-white'}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function AmortizationTable({ conventional, flatFee, showYearly }) {
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('yearly');

  const getData = () => {
    if (viewMode === 'yearly') {
      const convYearly = [];
      const ffYearly = [];
      for (let y = 1; y <= 30; y++) {
        const cIdx = y * 12 - 1;
        const fIdx = y * 12 - 1;
        if (conventional.rows[cIdx]) convYearly.push({ year: y, ...conventional.rows[cIdx] });
        if (flatFee.rows[fIdx]) ffYearly.push({ year: y, ...flatFee.rows[fIdx] });
      }
      return { conv: convYearly, ff: ffYearly };
    }
    // Monthly: show first 24, midpoint area, last 12
    const keyMonths = [
      ...Array.from({ length: 24 }, (_, i) => i),
      ...Array.from({ length: 12 }, (_, i) => 174 + i),
      ...Array.from({ length: 12 }, (_, i) => 348 + i),
    ];
    return {
      conv: keyMonths.map(i => conventional.rows[i]).filter(Boolean),
      ff: keyMonths.map(i => flatFee.rows[i]).filter(Boolean),
    };
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        data-testid="expand-amortization"
        className="w-full flex items-center justify-center gap-2 py-4 text-sm text-blue-400 hover:text-blue-300 transition-colors"
      >
        <Calculator className="w-4 h-4" />
        View Full Amortization Tables
        <ChevronDown className="w-4 h-4" />
      </button>
    );
  }

  const { conv, ff } = getData();

  return (
    <div data-testid="amortization-tables">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Amortization Comparison</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('yearly')}
            className={`px-3 py-1 rounded-full text-xs font-medium ${viewMode === 'yearly' ? 'bg-blue-500/20 text-blue-400' : 'bg-white/5 text-gray-500'}`}
          >
            Yearly
          </button>
          <button
            onClick={() => setViewMode('monthly')}
            className={`px-3 py-1 rounded-full text-xs font-medium ${viewMode === 'monthly' ? 'bg-blue-500/20 text-blue-400' : 'bg-white/5 text-gray-500'}`}
          >
            Key Months
          </button>
          <button
            onClick={() => setExpanded(false)}
            className="ml-2 text-gray-500 hover:text-white"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Conventional Table */}
        <div className="rounded-xl border border-white/5 overflow-hidden">
          <div className="bg-red-500/10 border-b border-white/5 px-4 py-2">
            <span className="text-sm font-semibold text-red-400">Conventional Loan</span>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/5 sticky top-0">
                <tr className="text-gray-500">
                  <th className="px-3 py-2 text-left">{viewMode === 'yearly' ? 'Year' : 'Month'}</th>
                  <th className="px-3 py-2 text-right">Payment</th>
                  <th className="px-3 py-2 text-right">Interest</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {conv.map((row, i) => (
                  <tr key={i} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-3 py-1.5 text-gray-400">{viewMode === 'yearly' ? row.year : row.month}</td>
                    <td className="px-3 py-1.5 text-right text-white font-mono">{fmtExact(row.payment)}</td>
                    <td className="px-3 py-1.5 text-right text-red-400/70 font-mono">{fmtExact(row.totalInterest)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{fmt(row.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Flat Fee Table */}
        <div className="rounded-xl border border-white/5 overflow-hidden">
          <div className="bg-emerald-500/10 border-b border-white/5 px-4 py-2">
            <span className="text-sm font-semibold text-emerald-400">Flat Fee Home Loan</span>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/5 sticky top-0">
                <tr className="text-gray-500">
                  <th className="px-3 py-2 text-left">{viewMode === 'yearly' ? 'Year' : 'Month'}</th>
                  <th className="px-3 py-2 text-right">Payment</th>
                  <th className="px-3 py-2 text-right">Total Cost</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ff.map((row, i) => (
                  <tr key={i} className={`border-t border-white/5 hover:bg-white/5 ${row.phase === 'flat-fee' ? 'bg-emerald-500/5' : ''}`}>
                    <td className="px-3 py-1.5 text-gray-400">
                      {viewMode === 'yearly' ? row.year : row.month}
                      {row.phase === 'flat-fee' && viewMode === 'yearly' && row.year === 16 && (
                        <span className="ml-1 text-[9px] text-emerald-400">FLAT FEE</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right text-white font-mono">{fmtExact(row.payment)}</td>
                    <td className="px-3 py-1.5 text-right text-emerald-400/70 font-mono">{fmtExact(row.totalInterest)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{fmt(row.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---
export default function KutroHome() {
  const [loanAmount, setLoanAmount] = useState(300000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [creditTier, setCreditTier] = useState(0); // index into creditTiers

  const flatFeePercent = creditTiers[creditTier].fee;

  const results = useMemo(() => {
    const conv = buildAmortization(loanAmount, interestRate / 100, 360);
    const ff = buildFlatFeeAmortization(loanAmount, interestRate / 100, flatFeePercent);

    const convTotal = loanAmount + conv.totalInterest;
    const ffTotal = loanAmount + ff.totalInterest;
    const savings = convTotal - ffTotal;
    const savingsPercent = ((savings / convTotal) * 100).toFixed(1);
    const interestSavings = conv.totalInterest - ff.totalInterest;
    const interestSavingsPercent = ((interestSavings / conv.totalInterest) * 100).toFixed(1);

    return { conv, ff, convTotal, ffTotal, savings, savingsPercent, interestSavings, interestSavingsPercent };
  }, [loanAmount, interestRate, flatFeePercent]);

  const generatePDF = useCallback(() => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pw = doc.internal.pageSize.getWidth();
    const tier = creditTiers[creditTier];
    const r = results;
    let y = 40;

    // Header
    doc.setFillColor(15, 80, 60);
    doc.rect(0, 0, pw, 80, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text('KOJIRU HOME', 40, y + 8);
    doc.setFontSize(11);
    doc.setTextColor(200, 240, 220);
    doc.text('Flat Fee Home Loan  |  Personalized Comparison Report', 40, y + 28);
    doc.setFontSize(8);
    doc.setTextColor(200, 200, 200);
    doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pw - 40, y + 28, { align: 'right' });

    y = 105;

    // Loan Parameters Box
    doc.setFillColor(240, 245, 243);
    doc.roundedRect(40, y, pw - 80, 55, 4, 4, 'F');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('LOAN AMOUNT', 60, y + 18);
    doc.text('INTEREST RATE', 230, y + 18);
    doc.text('CREDIT RATING', 400, y + 18);
    doc.setFontSize(14);
    doc.setTextColor(30, 30, 30);
    doc.text(fmt(loanAmount), 60, y + 40);
    doc.text(`${interestRate.toFixed(1)}%`, 230, y + 40);
    doc.text(`Grade ${tier.grade} (${tier.range}), ${tier.fee}% Flat Fee`, 400, y + 40);

    y += 80;

    // Savings Banner
    doc.setFillColor(220, 252, 238);
    doc.roundedRect(40, y, pw - 80, 60, 4, 4, 'F');
    doc.setFontSize(10);
    doc.setTextColor(6, 95, 70);
    doc.text('AMOUNT SAVED OVER 30 YEARS (ILLUSTRATIVE)', pw / 2, y + 18, { align: 'center' });
    doc.setFontSize(28);
    doc.text(fmt(r.savings), pw / 2, y + 46, { align: 'center' });

    y += 80;

    // Side-by-side comparison table
    doc.setFontSize(12);
    doc.setTextColor(30, 30, 30);
    doc.text('Loan Comparison Summary', 40, y);
    y += 15;

    autoTable(doc, {
      startY: y,
      margin: { left: 40, right: 40 },
      head: [['', 'Conventional 30-Year', 'Kojiru Flat Fee']],
      body: [
        ['Monthly Payment (Yrs 1-15)', fmtExact(r.conv.monthly), fmtExact(r.ff.monthlyFirst)],
        ['Monthly Payment (Yrs 16-30)', fmtExact(r.conv.monthly), fmtExact(r.ff.monthlySecond)],
        ['Flat Fee (added to balance, start of year 16)', 'None', fmt(r.ff.flatFee)],
        ['Total Interest / Fees Paid', fmt(r.conv.totalInterest), fmt(r.ff.totalInterest)],
        ['Total Loan Payoff', fmt(r.convTotal), fmt(r.ffTotal)],
        ['Amount Saved', '', fmt(r.savings)],
        ['Savings %', '', `${r.savingsPercent}%`],
      ],
      theme: 'grid',
      styles: { fillColor: [255, 255, 255], textColor: [40, 40, 40], lineColor: [200, 200, 200], fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: [240, 245, 243], textColor: [6, 95, 70], fontStyle: 'bold' },
      columnStyles: { 0: { fontStyle: 'bold', textColor: [80, 80, 80] } },
      alternateRowStyles: { fillColor: [248, 250, 249] },
    });

    y = doc.lastAutoTable.finalY + 25;

    // Credit Tier Comparison
    doc.setFontSize(12);
    doc.setTextColor(30, 30, 30);
    doc.text('Savings by Credit Rating', 40, y);
    y += 15;

    const tierRows = creditTiers.map(t => {
      const ff = buildFlatFeeAmortization(loanAmount, interestRate / 100, t.fee);
      const ffTot = loanAmount + ff.totalInterest;
      const sav = r.convTotal - ffTot;
      return [`Grade ${t.grade} (${t.range})`, `${t.fee}%`, fmt(loanAmount * t.fee / 100), fmt(ffTot), fmt(sav)];
    });

    autoTable(doc, {
      startY: y,
      margin: { left: 40, right: 40 },
      head: [['Credit Rating', 'Flat Fee %', 'Flat Fee $', 'Total Payoff', 'You Save']],
      body: tierRows,
      theme: 'grid',
      styles: { fillColor: [255, 255, 255], textColor: [40, 40, 40], lineColor: [200, 200, 200], fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: [240, 245, 243], textColor: [6, 95, 70], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 249] },
    });

    y = doc.lastAutoTable.finalY + 25;

    // How it works and notices
    doc.setFillColor(255, 251, 235);
    doc.roundedRect(40, y, pw - 80, 62, 4, 4, 'F');
    doc.setFontSize(9);
    doc.setTextColor(180, 120, 0);
    doc.text('How the numbers work', 55, y + 14);
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8);
    doc.text('At the start of year 16 the flat fee is added to the balance, and the new total is repaid in 180 equal', 55, y + 27);
    doc.text('monthly payments with no interest. No lump sum, no balloon payment. Savings apply only if the loan is', 55, y + 38);
    doc.text('still open at the start of year 16. Kojiru does not lend money and does not offer this loan.', 55, y + 49);

    // --- Page 2: Amortization Table ---
    doc.addPage();
    y = 40;

    doc.setFillColor(15, 80, 60);
    doc.rect(0, 0, pw, 60, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text('Amortization Schedule: Yearly Breakdown', 40, y + 16);
    doc.setFontSize(8);
    doc.setTextColor(200, 200, 200);
    doc.text(`${fmt(loanAmount)} @ ${interestRate.toFixed(1)}%  |  Grade ${tier.grade} (${tier.fee}% Flat Fee)`, 40, y + 32);

    y = 75;

    // Build yearly amortization rows
    const amortRows = [];
    for (let yr = 1; yr <= 30; yr++) {
      const cRow = r.conv.rows[yr * 12 - 1];
      const fRow = r.ff.rows[yr * 12 - 1];
      if (cRow && fRow) {
        amortRows.push([
          yr.toString(),
          fmtExact(cRow.payment),
          fmt(cRow.totalInterest),
          fmt(cRow.balance),
          fmtExact(fRow.payment),
          fmt(fRow.totalInterest),
          fmt(fRow.balance),
        ]);
      }
    }

    autoTable(doc, {
      startY: y,
      margin: { left: 30, right: 30 },
      head: [['Year', 'Conv. Payment', 'Conv. Interest', 'Conv. Balance', 'FF Payment', 'FF Total Cost', 'FF Balance']],
      body: amortRows,
      theme: 'grid',
      styles: { fillColor: [255, 255, 255], textColor: [40, 40, 40], lineColor: [200, 200, 200], fontSize: 7, cellPadding: 4, halign: 'right' },
      headStyles: { fillColor: [240, 245, 243], textColor: [6, 95, 70], fontStyle: 'bold', halign: 'center', fontSize: 7 },
      columnStyles: { 0: { halign: 'center', fontStyle: 'bold', textColor: [80, 80, 80] } },
      alternateRowStyles: { fillColor: [248, 250, 249] },
      didParseCell: (data) => {
        // Highlight the flat-fee transition at year 16
        if (data.row.index >= 15 && data.column.index >= 4) {
          data.cell.styles.textColor = [6, 95, 70];
        }
      },
    });

    // Footer on last page
    const lastPage = doc.internal.getNumberOfPages();
    for (let i = 1; i <= lastPage; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(140, 140, 140);
      doc.text('Kojiru Home  |  kojiru.com  |  Illustrative only. Not a loan offer or financial advice. No savings are guaranteed.', pw / 2, doc.internal.pageSize.getHeight() - 20, { align: 'center' });
      doc.text(`Page ${i} of ${lastPage}`, pw - 40, doc.internal.pageSize.getHeight() - 20, { align: 'right' });
    }

    doc.save(`Kojiru-Home-Loan-Report-${fmt(loanAmount).replace(/[^0-9]/g, '')}-${tier.grade}.pdf`);
  }, [loanAmount, interestRate, creditTier, results]);

  const generateAmortizationPDF = useCallback(() => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    const pw = doc.internal.pageSize.getWidth();
    const tier = creditTiers[creditTier];
    const r = results;
    let y = 40;

    // Cover header
    doc.setFillColor(15, 80, 60);
    doc.rect(0, 0, pw, 70, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text('KOJIRU HOME', 40, y + 6);
    doc.setFontSize(13);
    doc.setTextColor(200, 240, 220);
    doc.text('360-Month Amortization Schedule  |  Conventional vs. Flat Fee', 40, y + 26);
    doc.setFontSize(9);
    doc.setTextColor(200, 200, 200);
    doc.text(`${fmt(loanAmount)} @ ${interestRate.toFixed(1)}%  |  Grade ${tier.grade} (${tier.fee}% Flat Fee)  |  Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 40, y + 42);

    y = 85;

    // Summary bar
    doc.setFillColor(220, 252, 238);
    doc.roundedRect(40, y, pw - 80, 40, 4, 4, 'F');
    doc.setFontSize(9);
    doc.setTextColor(6, 95, 70);
    doc.text('AMOUNT SAVED (ILLUSTRATIVE)', 60, y + 16);
    doc.setFontSize(18);
    doc.text(fmt(r.savings), 60, y + 32);
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text(`Conventional Total: ${fmt(r.convTotal)}  |  Flat Fee Total: ${fmt(r.ffTotal)}  |  Savings: ${r.savingsPercent}%`, 240, y + 24);

    y += 55;

    // Build 360-month rows
    const monthRows = [];
    let convCumPaid = 0;
    let ffCumPaid = 0;

    for (let m = 0; m < 360; m++) {
      const cRow = r.conv.rows[m];
      const fRow = r.ff.rows[m];
      if (!cRow || !fRow) continue;

      convCumPaid += cRow.payment;
      ffCumPaid += fRow.payment;

      const cumSavings = convCumPaid - ffCumPaid;

      monthRows.push([
        (m + 1).toString(),
        fmtExact(cRow.payment),
        fmtExact(cRow.principal),
        fmtExact(cRow.interest),
        fmt(Math.max(0, cRow.balance)),
        fRow.phase === 'flat-fee' ? fmtExact(fRow.payment) : fmtExact(fRow.payment),
        fmtExact(fRow.principal),
        fRow.phase === 'standard' ? fmtExact(fRow.interest) : (m === 180 ? fmt(r.ff.flatFee) : '$0.00'),
        fmt(Math.max(0, fRow.balance)),
        fmt(cumSavings),
      ]);
    }

    autoTable(doc, {
      startY: y,
      margin: { left: 25, right: 25 },
      head: [[
        { content: 'Mo.', styles: { halign: 'center' } },
        { content: 'Conv.\nPayment', styles: { halign: 'right' } },
        { content: 'Conv.\nPrincipal', styles: { halign: 'right' } },
        { content: 'Conv.\nInterest', styles: { halign: 'right' } },
        { content: 'Conv.\nBalance', styles: { halign: 'right' } },
        { content: 'Flat Fee\nPayment', styles: { halign: 'right' } },
        { content: 'Flat Fee\nPrincipal', styles: { halign: 'right' } },
        { content: 'Flat Fee\nInt. / Fee Added', styles: { halign: 'right' } },
        { content: 'Flat Fee\nBalance', styles: { halign: 'right' } },
        { content: 'Cumul.\nSavings', styles: { halign: 'right' } },
      ]],
      body: monthRows,
      theme: 'grid',
      styles: {
        fillColor: [255, 255, 255],
        textColor: [40, 40, 40],
        lineColor: [210, 210, 210],
        fontSize: 6,
        cellPadding: 2.5,
        halign: 'right',
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [240, 245, 243],
        textColor: [6, 95, 70],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 6.5,
        cellPadding: 4,
      },
      columnStyles: {
        0: { halign: 'center', fontStyle: 'bold', textColor: [80, 80, 80], cellWidth: 30 },
      },
      alternateRowStyles: { fillColor: [248, 250, 249] },
      didParseCell: (data) => {
        if (data.section === 'body') {
          // Highlight flat-fee phase rows (months 181-360)
          if (data.row.index >= 180 && data.column.index >= 5 && data.column.index <= 8) {
            data.cell.styles.textColor = [6, 95, 70];
          }
          // Highlight cumulative savings column
          if (data.column.index === 9) {
            data.cell.styles.textColor = [6, 120, 70];
            data.cell.styles.fontStyle = 'bold';
          }
          // Highlight year 15 transition row
          if (data.row.index === 179) {
            data.cell.styles.fillColor = [255, 251, 235];
          }
        }
      },
    });

    // Footer on every page
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const ph = doc.internal.pageSize.getHeight();
      doc.setFontSize(6.5);
      doc.setTextColor(100, 100, 100);
      doc.text(
        '\u00A9 2026 Kojiru, Inc. All Rights Reserved. The Kojiru Home Flat Fee Home Loan concept, structure, and methodology are proprietary and protected under applicable intellectual property laws.',
        pw / 2, ph - 18, { align: 'center' }
      );
      doc.text(`Page ${i} of ${totalPages}`, pw - 30, ph - 18, { align: 'right' });
    }

    doc.save(`Kojiru-Home-Amortization-360mo-${fmt(loanAmount).replace(/[^0-9]/g, '')}-${tier.grade}.pdf`);
  }, [loanAmount, interestRate, creditTier, results]);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 via-transparent to-transparent pointer-events-none" />
        <div className="container mx-auto max-w-5xl relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-4" data-testid="home-status-badge">
              Design paper. Not a loan offer.
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6 ml-2">
              <Home className="w-4 h-4" />
              Kojiru Home
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="home-hero-title">
              A Normal Mortgage for 15 Years.
              <br />
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Then No Interest for 15.
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-4">
              The Flat Fee Home Loan replaces the interest in years 16 to 30 with one flat fee, known on day one. No lump sum and no balloon payment. On a $300,000 loan at 7.5%, a borrower would pay $76,296 to $121,296 less over 30 years, depending on credit tier.
            </p>
            <p className="text-xs text-amber-400/60 max-w-xl mx-auto mb-2" data-testid="home-regulatory-note">
              Kojiru does not lend money and does not offer this loan. Before any loan is offered, licensed lenders and their counsel must confirm how the design fits federal and state lending rules, including disclosure and qualified mortgage rules. All figures are illustrative.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/downloads/kojiru-home-whitepaper-v2.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-whitepaper"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition-colors"
              >
                <Download className="w-4 h-4" />
                Read the White Paper
              </a>
              <a
                href="/downloads/kojiru-infographic-flat-fee-home-loan.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-one-page-guide"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Download className="w-4 h-4" />
                One-Page Guide
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works: 3 Steps */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Landmark, title: 'Years 1 to 15', desc: 'A normal 30-year mortgage. Same payment, same rules. Selling or refinancing works the usual way.', accent: 'text-blue-400' },
              { icon: DollarSign, title: 'Start of Year 16', desc: 'The interest stops. One flat fee, set at closing by credit tier, is added to the balance. Nothing extra is paid that day.', accent: 'text-emerald-400' },
              { icon: TrendingDown, title: 'Years 16 to 30', desc: 'The new balance is repaid in 180 equal monthly payments with no interest. No lump sum and no balloon payment.', accent: 'text-cyan-400' },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-xl bg-white/[0.02] border border-white/5"
              >
                <step.icon className={`w-8 h-8 ${step.accent} mb-4`} />
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Calculator */}
      <section className="py-16 px-6" id="calculator">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              Loan Comparison <span className="text-emerald-400">Calculator</span>
            </h2>
            <p className="text-sm text-gray-400">Adjust the inputs to compare a normal 30-year mortgage with the Flat Fee design. Illustrative only.</p>
          </motion.div>

          {/* Controls */}
          <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 mb-8" data-testid="calculator-controls">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Loan Amount */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Loan Amount</label>
                <div className="text-2xl font-bold text-white mb-2">{fmt(loanAmount)}</div>
                <input
                  type="range"
                  min={50000}
                  max={1000000}
                  step={10000}
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  data-testid="loan-amount-slider"
                  className="w-full accent-emerald-500"
                />
                <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                  <span>$50K</span>
                  <span>$1M</span>
                </div>
              </div>

              {/* Interest Rate */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Interest Rate</label>
                <div className="text-2xl font-bold text-white mb-2">{interestRate.toFixed(1)}%</div>
                <input
                  type="range"
                  min={3}
                  max={12}
                  step={0.25}
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  data-testid="interest-rate-slider"
                  className="w-full accent-emerald-500"
                />
                <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                  <span>3%</span>
                  <span>12%</span>
                </div>
              </div>

              {/* Credit Tier */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Credit Rating</label>
                <div className="space-y-2">
                  {creditTiers.map((tier, idx) => (
                    <button
                      key={tier.grade}
                      onClick={() => setCreditTier(idx)}
                      data-testid={`credit-tier-${tier.grade.toLowerCase()}`}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-all ${
                        creditTier === idx
                          ? `${tier.bg} ${tier.border} ${tier.color} font-medium`
                          : 'bg-white/5 border-white/5 text-gray-500 hover:bg-white/10'
                      }`}
                    >
                      <span>Grade {tier.grade} <span className="text-xs opacity-60">({tier.range})</span></span>
                      <span className="font-mono text-xs">{tier.fee}% fee</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Results: Savings Banner */}
          <motion.div
            key={`${loanAmount}-${interestRate}-${flatFeePercent}`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-cyan-500/10 border border-emerald-500/20 rounded-2xl p-6 mb-8 text-center"
            data-testid="savings-banner"
          >
            <div className="text-sm text-emerald-400/80 mb-1 uppercase tracking-wider">Amount Saved Over 30 Years (Illustrative)</div>
            <div className="text-4xl sm:text-5xl font-bold text-emerald-400 mb-2">{fmt(results.savings)}</div>
            <div className="text-sm text-gray-400">
              {results.savingsPercent}% less paid in total than a normal mortgage, if the loan is still open at the start of year 16
            </div>
            <button
              onClick={generatePDF}
              data-testid="download-pdf-btn"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm font-medium hover:bg-emerald-500/30 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download Report
            </button>
            <button
              onClick={generateAmortizationPDF}
              data-testid="download-amortization-btn"
              className="inline-flex items-center gap-2 mt-4 ml-2 px-5 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium hover:bg-cyan-500/20 transition-colors"
            >
              <Calculator className="w-4 h-4" />
              360-Month Schedule
            </button>
          </motion.div>

          {/* Side by Side Comparison */}
          <div className="grid md:grid-cols-2 gap-6 mb-8" data-testid="loan-comparison">
            {/* Conventional */}
            <div className="rounded-2xl border border-red-500/10 bg-red-500/[0.02] p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <h3 className="font-semibold text-red-400">Conventional 30-Year</h3>
              </div>
              <div className="space-y-3">
                <StatCard label="Monthly Payment" value={fmtExact(results.conv.monthly)} sub="Fixed for 30 years" />
                <StatCard label="Total Interest Paid" value={fmt(results.conv.totalInterest)} />
                <StatCard label="Total Loan Payoff" value={fmt(results.convTotal)} sub={`${fmt(results.conv.totalInterest)} interest + ${fmt(loanAmount)} principal`} />
              </div>
            </div>

            {/* Flat Fee */}
            <div className="rounded-2xl border border-emerald-500/10 bg-emerald-500/[0.02] p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <h3 className="font-semibold text-emerald-400">Kojiru Flat Fee Home Loan</h3>
              </div>
              <div className="space-y-3">
                <StatCard label="Monthly Payment (Yrs 1-15)" value={fmtExact(results.ff.monthlyFirst)} sub="Standard amortization" />
                <StatCard
                  label="Monthly Payment (Yrs 16-30)"
                  value={fmtExact(results.ff.monthlySecond)}
                  sub="No interest. Includes the flat fee"
                  accent
                />
                <StatCard
                  label="Flat Fee (added at start of year 16)"
                  value={fmt(results.ff.flatFee)}
                  sub={`${flatFeePercent}% of original loan (Grade ${creditTiers[creditTier].grade}). Added to the balance, not paid as a lump sum`}
                  accent
                />
                <StatCard label="Total Loan Payoff" value={fmt(results.ffTotal)} sub={`${fmt(results.ff.totalInterest)} total cost + ${fmt(loanAmount)} principal`} accent />
              </div>
            </div>
          </div>

          {/* Amortization Tables */}
          <AmortizationTable conventional={results.conv} flatFee={results.ff} />
        </div>
      </section>

      {/* Credit Tier Breakdown */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Credit Rating Scale</h2>
            <p className="text-sm text-gray-400">Better credit means a lower flat fee. Every tier pays less in total than a normal mortgage when the loan is still open at the start of year 16.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {creditTiers.map((tier) => {
              const ff = buildFlatFeeAmortization(loanAmount, interestRate / 100, tier.fee);
              const conv = results.conv;
              const convTotal = loanAmount + conv.totalInterest;
              const ffTotal = loanAmount + ff.totalInterest;
              const sav = convTotal - ffTotal;
              return (
                <motion.div
                  key={tier.grade}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className={`rounded-xl ${tier.bg} border ${tier.border} p-6`}
                  data-testid={`tier-card-${tier.grade.toLowerCase()}`}
                >
                  <div className={`text-3xl font-bold ${tier.color} mb-1`}>Grade {tier.grade}</div>
                  <div className="text-sm text-gray-400 mb-4">Credit Score {tier.range}</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Flat Fee</span>
                      <span className="text-white font-medium">{tier.fee}% = {fmt(loanAmount * tier.fee / 100)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Payoff</span>
                      <span className="text-white font-medium">{fmt(ffTotal)}</span>
                    </div>
                    <div className={`flex justify-between pt-2 border-t border-white/5`}>
                      <span className="text-gray-500">You Save</span>
                      <span className={`font-bold ${tier.color}`}>{fmt(sav)}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Under Study and Technology */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl grid md:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="rounded-2xl bg-white/[0.02] border border-white/5 p-6" data-testid="features-under-study">
            <h2 className="text-xl font-bold mb-3">Features Under Study</h2>
            <p className="text-sm text-gray-400 mb-4">These ideas are being studied. They are not part of any loan offered today, and none is a guarantee.</p>
            <ul className="space-y-3 text-sm text-gray-400">
              <li><span className="text-white font-medium">Hardship reserve:</span> a small amount set aside from each loan into a shared reserve a lender could use to help borrowers through short periods of hardship. It would be run by the lender under its own rules. It would not be insurance, and help could not be promised.</li>
              <li><span className="text-white font-medium">Early payoff reward:</span> a lender could choose to reward borrowers who pay the loan off within 15 years.</li>
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="rounded-2xl bg-white/[0.02] border border-white/5 p-6" data-testid="home-technology">
            <h2 className="text-xl font-bold mb-3">Technology</h2>
            <p className="text-sm text-gray-400 mb-4">Kojiru is designing software, including AI assistants, to help lenders with steps such as:</p>
            <ul className="space-y-2 text-sm text-gray-400 list-disc ml-5">
              <li>Checking documents</li>
              <li>Comparing loan options for a borrower's situation</li>
              <li>Managing the change at the start of year 16</li>
            </ul>
            <p className="text-xs text-gray-500 mt-4">Lenders would remain responsible for every lending decision. Loan terms, including the flat fee percentages, would be set by licensed lenders, not by Kojiru and not by any token holder vote.</p>
          </motion.div>
        </div>
      </section>

      {/* Featured Article */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Link to="/blog#amortization-ceiling" data-testid="featured-article-link"
              className="block group rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-emerald-500/30 hover:bg-white/[0.03] transition-all overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-[10px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Featured Article</span>
                  <span className="text-xs text-gray-500">8 min read</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-emerald-300 transition-colors mb-3">
                  The Amortization Ceiling: Why the Next Great Banking Pivot Isn't a Rate Cut. It's a Change in Structure.
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-4">
                  For a Chief Risk Officer at a regional bank, the challenge has always been: how do we innovate without inviting a regulatory storm? The answer isn't a new marketing campaign. It's a structural shift that reimagines the second half of the loan's life.
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-400 group-hover:gap-2.5 transition-all">
                  Read the full article <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Study the Design
            </h2>
            <p className="text-gray-400 mb-8">
              The white paper explains every number on this page. Community banks, credit unions, and other lenders are welcome to study it and send questions.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/downloads/kojiru-home-whitepaper-v2.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition-colors"
                data-testid="cta-whitepaper"
              >
                <Download className="w-4 h-4" />
                Read the White Paper
              </a>
              <a
                href="#calculator"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-400 font-medium transition-colors"
                data-testid="cta-calculator"
              >
                <Calculator className="w-4 h-4" />
                Try the Calculator
              </a>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 hover:bg-white/5 text-white font-medium transition-colors"
                data-testid="cta-contact-home"
              >
                Lender Inquiries
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Copyright Notice */}
      <section className="pb-4 px-6">
        <div className="container mx-auto max-w-5xl text-center">
          <p className="text-[10px] text-gray-600" data-testid="copyright-notice">
            &copy; 2026 Kojiru, Inc. All Rights Reserved. The Kojiru Home Flat Fee Home Loan concept, structure, and methodology are proprietary and protected under applicable intellectual property laws.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
